// Homepage recommendations are editorial. Persisted catalog status controls
// discovery; direct links and owners' libraries keep the full visible corpus.

type CatalogGame = {
  readonly coworld_name?: string | null;
  readonly name?: string | null;
  readonly game_address?: {
    readonly catalog_status: "listed" | "unlisted";
  } | null;
};

// Only persisted catalog membership can put a game in discovery.
export function catalogListedLeagues<T extends { readonly game: CatalogGame }>(
  leagues: readonly T[],
): T[] {
  return leagues.filter(
    (league) => league.game.game_address?.catalog_status === "listed",
  );
}

export function normalizeCoworldKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, "");
}

export const curatedCoworldKeys: readonly string[] = [
  normalizeCoworldKey("generals-competition"),
  normalizeCoworldKey("Gods of the Arena"),
  normalizeCoworldKey("Light vs Dark"),
  normalizeCoworldKey("Call to Adventure"),
  normalizeCoworldKey("paintbot-pw"),
  normalizeCoworldKey("battlecode"),
  normalizeCoworldKey("proxywar"),
  normalizeCoworldKey("harvestbench"),
];

const curatedKeySet = new Set(curatedCoworldKeys);

export function curationOn(): boolean {
  return process.env.COWORLD_CURATION !== "off";
}

export function isCuratedCoworld(key: string | null | undefined): boolean {
  if (!curationOn()) return true;

  if (key == null) return false;

  return curatedKeySet.has(normalizeCoworldKey(key));
}

export function curatedLeagues<T extends { readonly game: CatalogGame }>(
  leagues: readonly T[],
): T[] {
  const listed = catalogListedLeagues(leagues);

  if (!curationOn()) return listed;

  return listed.filter((league) =>
    isCuratedCoworld(league.game.coworld_name ?? league.game.name ?? null),
  );
}

// Display corrections remain active when recommendation curation is disabled.
const curatedNames: Record<string, string> = {
  [normalizeCoworldKey("paintbot-pw")]: "Paintbot",
  [normalizeCoworldKey("Gods of the Arena")]: "Gods of the Arena",
  [normalizeCoworldKey("proxywar")]: "Proxy War",
};

export function curatedCoworldName(
  key: string | null | undefined,
): string | null {
  if (key == null) return null;

  return curatedNames[normalizeCoworldKey(key)] ?? null;
}

// Persisted records and replay descriptions can contain either historical spelling.
const curatedLeagueNames: Record<string, string> = {
  "Paintbot Royal": "Paintbot 2",
  "Paintbot Royale": "Paintbot 2",
};

export function curatedLeagueName(league: {
  readonly id?: string;
  readonly name: string;
}): string {
  return curatedLeagueNames[league.name] ?? league.name;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Prose uses literal league names; normalized Coworld keys are not prose matches.
export function curatedText(text: string): string {
  let result = text;

  for (const [corpusName, displayName] of Object.entries(curatedLeagueNames)) {
    result = result.replace(
      new RegExp(`\\b${escapeRegExp(corpusName)}\\b`, "g"),
      displayName,
    );
  }

  return result;
}

const curatedGuideUrls: Record<string, string> = {
  [normalizeCoworldKey("generals-competition")]:
    "https://softmax.com/docs/guides/generals",
  [normalizeCoworldKey("generals")]: "https://softmax.com/docs/guides/generals",
  [normalizeCoworldKey("Gods of the Arena")]:
    "https://softmax-universe.github.io/polyworld-buff/GOTA",
};

export function curatedGuideUrl(key: string | null | undefined): string | null {
  if (key == null) return null;

  return curatedGuideUrls[normalizeCoworldKey(key)] ?? null;
}
